/**
 * Interactive HTML Renderer
 *
 * Main component that uses Interweave to render HTML with interactive enhancements.
 * Processes data-interactive attributes and replaces them with React components.
 */

'use client';

import { Interweave } from 'interweave';
import { polyfill } from 'interweave-ssr';
import React from 'react';

import { ModalProvider } from './ModalContext';
import { interactiveHtmlTransform } from './transform';
import type { ModalConfig } from './types';

import { sanitizeInlineStyles } from '@lib/html/sanitizeInlineStyles';

// Apply polyfill for SSR - REQUIRED even for client components
polyfill();

interface InteractiveHtmlRendererProps {
  content: string;
  className?: string;
  renderModal?: (config: ModalConfig, close: () => void) => React.ReactNode;
}

/**
 * Sanitize modal triggers to prevent hydration issues
 * Replace buttons with spans since they might be inside paragraph tags
 */
const sanitizeModalTriggers = (html: string): string => {
  // Replace <button> with <span> for modal triggers to prevent block-in-inline hydration issues
  // The transform will still handle making them interactive
  let sanitized = html;

  // Replace <button...data-interactive="modal"...> with <span...>
  sanitized = sanitized.replace(/<button([^>]*data-interactive="modal"[^>]*)>(.*?)<\/button>/gi, '<span$1>$2</span>');

  return sanitized;
};

export const InteractiveHtmlRenderer: React.FC<InteractiveHtmlRendererProps> = ({
  content,
  className,
  renderModal,
}) => {
  // Sanitize modal triggers to prevent hydration issues
  const sanitizedContent = sanitizeInlineStyles(sanitizeModalTriggers(content));

  return (
    <ModalProvider renderModal={renderModal}>
      <div className={className}>
        <Interweave content={sanitizedContent} transform={interactiveHtmlTransform} />
      </div>
    </ModalProvider>
  );
};

export default InteractiveHtmlRenderer;
